import React, { useState, useEffect } from 'react';
import { tiktokApi, TikTokAccount, TikTokStatus, TikTokUser } from '../services/tiktok-api';
import { extractErrorMessage } from '../services/api';
import './TikTokUploadPanel.css';

interface TikTokUploadPanelProps {
  videoUrl?: string;
  videoTitle?: string;
}

export function TikTokUploadPanel({ videoUrl, videoTitle }: TikTokUploadPanelProps) {
  const [status, setStatus] = useState<TikTokStatus | null>(null);
  const [user, setUser] = useState<TikTokUser | null>(null);
  const [account, setAccount] = useState('default');
  const [accounts, setAccounts] = useState<TikTokAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [hook, setHook] = useState(videoTitle || '');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [inputVideoUrl, setInputVideoUrl] = useState(videoUrl || '');
  const [uploadingStatus, setUploadingStatus] = useState<string | null>(null);
  const [publishId, setPublishId] = useState<string | null>(null);
  const [publishAccount, setPublishAccount] = useState('default');
  const [preparedCaption, setPreparedCaption] = useState<string | null>(null);

  const draftCaption = [hook.trim(), caption.trim(), hashtags.trim()].filter(Boolean).join('\n\n');

  // Reload account state when the selected Studio account key changes.
  useEffect(() => {
    loadStatus();
  }, [account]);

  // Poll publish status if we have a publishId
  useEffect(() => {
    if (!publishId) return;

    const interval = setInterval(() => {
      checkPublishStatus(publishId, publishAccount);
    }, 2000);

    return () => clearInterval(interval);
  }, [publishId]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const [response, accountsResponse] = await Promise.all([
        tiktokApi.getStatus(account),
        tiktokApi.getAccounts(),
      ]);
      const statusData = response.data as TikTokStatus;
      setStatus(statusData);
      setAccounts(accountsResponse.data?.accounts || []);

      if (statusData.authorized) {
        try {
          const userResponse = await tiktokApi.getUser(account);
          setUser(userResponse.data?.user || null);
        } catch {
          setUser(null);
        }
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load TikTok status'));
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorize = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await tiktokApi.getAuthUrl(account);
      const { authUrl } = response.data;

      // Open auth URL in a popup window
      const popup = window.open(authUrl, 'tiktok-auth', 'width=500,height=600');

      // Poll for completion
      const checkInterval = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(checkInterval);
          // Reload status after a delay
          setTimeout(() => {
            loadStatus();
          }, 2000);
        }
      }, 500);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to start authorization'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setUploadingStatus('Initializing video upload...');

      if (!draftCaption) {
        setError('Add a hook, caption, or hashtags for the TikTok draft');
        setLoading(false);
        return;
      }

      if (!inputVideoUrl) {
        setError('Video URL is required');
        setLoading(false);
        return;
      }

      const response = await tiktokApi.initVideoUpload(inputVideoUrl, account);

      const { publishId: newPublishId } = response.data;
      setPublishId(newPublishId);
      setPublishAccount(account);
      setPreparedCaption(draftCaption);
      setUploadingStatus('Video sent to the TikTok inbox. Finish the draft in the TikTok app.');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to upload video'));
      setUploadingStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const checkPublishStatus = async (id: string, statusAccount: string) => {
    try {
      const response = await tiktokApi.getPublishStatus(id, statusAccount);
      const { status: publishStatus, videoId, error: publishError } = response.data;

      if (publishStatus === 'PUBLISHED') {
        setSuccess(`TikTok draft is ready. Complete the caption, product link, disclosure, and publishing in the TikTok app. ${videoId ? `Video ID: ${videoId}` : ''}`);
        setUploadingStatus(null);
        setPublishId(null);
      } else if (publishStatus === 'FAILED') {
        setError(`Upload failed: ${publishError}`);
        setUploadingStatus(null);
        setPublishId(null);
      } else {
        setUploadingStatus(`Status: ${publishStatus}`);
      }
    } catch (err: any) {
      console.error('Failed to check publish status:', err);
    }
  };

  const handleRefreshToken = async () => {
    try {
      setLoading(true);
      await tiktokApi.refreshToken(account);
      setSuccess('Token refreshed successfully');
      await loadStatus();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to refresh token'));
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    try {
      setLoading(true);
      await tiktokApi.revoke(account);
      setSuccess('Authorization revoked');
      await loadStatus();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to revoke authorization'));
    } finally {
      setLoading(false);
    }
  };

  if (!status) {
    return <div className="tiktok-panel">Loading TikTok status...</div>;
  }

  if (!status.configured) {
    return (
      <div className="tiktok-panel error-box">
        <h3>TikTok Not Configured</h3>
        <p>
          TikTok integration is not configured. Please set the following environment variables:
        </p>
        <ul>
          <li>TIKTOK_CLIENT_KEY</li>
          <li>TIKTOK_CLIENT_SECRET</li>
          <li>TIKTOK_REDIRECT_URI</li>
        </ul>
        <p>
          Get these from the{' '}
          <a href="https://developer.tiktok.com/" target="_blank" rel="noopener noreferrer">
            TikTok Developer Portal
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="tiktok-panel">
      <div className="tiktok-header">
        <h2>🎵 TikTok Upload</h2>
      </div>

      {error && <div className="tiktok-error">{error}</div>}
      {success && <div className="tiktok-success">{success}</div>}

      {status.authorized && !status.canUploadToInbox && (
        <div className="tiktok-error">
          This connection does not have <code>video.upload</code>. Disconnect and reconnect to authorize inbox drafts.
        </div>
      )}

      <div className="form-group">
        <label htmlFor="tiktok-account-key">Studio account key</label>
        <input
          id="tiktok-account-key"
          list="tiktok-connected-accounts"
          value={account}
          onChange={(event) => setAccount(event.target.value.trim())}
          placeholder="default or second-channel"
          disabled={loading || uploadingStatus !== null}
          pattern="[A-Za-z0-9_-]{1,64}"
        />
        <datalist id="tiktok-connected-accounts">
          {accounts.map((connectedAccount) => <option key={connectedAccount.id} value={connectedAccount.id} />)}
        </datalist>
        <small>Use a new key to connect another TikTok channel. Keys allow letters, numbers, hyphens, and underscores.</small>
      </div>

      {!status.authorized || !status.canUploadToInbox ? (
        <div className="auth-section">
          <p>{status.authorized ? 'Reconnect your TikTok account to authorize inbox draft uploads' : 'Connect your TikTok account to upload videos'}</p>
          <button onClick={handleAuthorize} disabled={loading} className="btn-primary">
            {loading ? 'Connecting...' : 'Connect TikTok Account'}
          </button>
        </div>
      ) : (
        <>
          <div className="user-info">
            {user && (
              <div className="user-profile">
                <img src={user.avatar_url} alt={user.display_name} className="avatar" />
                <div>
                  <p className="username">{user.display_name}</p>
                  {user.is_verified && <span className="verified">✓ Verified</span>}
                </div>
              </div>
            )}

            <div className="token-info">
              {status.tokenExpired && <span className="token-expired">Token expired</span>}
              {status.expiresAt && (
                <p className="token-expiry">
                  Expires: {new Date(status.expiresAt).toLocaleString()}
                </p>
              )}
            </div>

            <div className="button-group">
              {status.tokenExpired && (
                <button onClick={handleRefreshToken} disabled={loading} className="btn-secondary">
                  Refresh Token
                </button>
              )}
              <button onClick={handleRevoke} disabled={loading} className="btn-secondary">
                Disconnect
              </button>
            </div>
          </div>

          <div className="upload-section">
            <h3>Send to TikTok Draft</h3>

            <div className="form-group">
              <label>Video URL *</label>
              <input
                type="url"
                value={inputVideoUrl}
                onChange={(e) => setInputVideoUrl(e.target.value)}
                placeholder="https://example.com/video.mp4"
                disabled={loading || uploadingStatus !== null}
              />
              <small>Public HTTPS video URL. TikTok sends it to your creator inbox as a draft.</small>
            </div>

            <div className="form-group">
              <label>Hook</label>
              <input
                type="text"
                value={hook}
                onChange={(e) => setHook(e.target.value)}
                placeholder="First line for the TikTok draft"
                disabled={loading || uploadingStatus !== null}
                maxLength={150}
              />
              <small>{hook.length}/150 characters</small>
            </div>

            <div className="form-group">
              <label>Caption</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Editable caption to paste into the TikTok draft"
                disabled={loading || uploadingStatus !== null}
                rows={3}
                maxLength={2000}
              />
              <small>{caption.length}/2000 characters</small>
            </div>

            <div className="form-group">
              <label>Hashtags and mentions</label>
              <input
                type="text"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="#banhlava #annalala @creator"
                disabled={loading || uploadingStatus !== null}
                maxLength={500}
              />
              <small>Separate every hashtag or mention with spaces or new lines.</small>
            </div>

            {preparedCaption && (
              <div className="tiktok-success">
                <strong>Paste into TikTok draft:</strong>
                <pre className="tiktok-caption-preview">{preparedCaption}</pre>
              </div>
            )}

            {uploadingStatus ? (
              <div className="uploading">
                <div className="spinner"></div>
                <p>{uploadingStatus}</p>
              </div>
            ) : (
              <button
                onClick={handleUpload}
                disabled={loading || !status.canUploadToInbox || !draftCaption || !inputVideoUrl}
                className="btn-primary"
              >
                {loading ? 'Sending...' : 'Send to TikTok Draft'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
